/**
 * Google Analytics 4 integration.
 *
 * - initGA() loads the gtag.js script and configures the measurement ID.
 * - trackEvent() fires custom GA4 events (no-op when GA is not loaded).
 * - usePageTracking() tracks page views on every route change.
 *
 * GA only loads when VITE_GA_MEASUREMENT_ID is set at build time.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

let initialized = false;

/**
 * Load the gtag.js script and configure GA4.
 * Safe to call multiple times — only initializes once.
 * No-op when VITE_GA_MEASUREMENT_ID is not set.
 */
export function initGA(): void {
  if (initialized || !GA_MEASUREMENT_ID) return;

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We track page views manually via usePageTracking
  });

  // Load gtag.js script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  script.onerror = () => {
    console.warn('[analytics] Failed to load gtag.js — GA events will not be sent.');
  };
  document.head.appendChild(script);

  initialized = true;
}

/**
 * Send a custom GA4 event.
 * No-op when GA is not initialized. Never throws — analytics must not break business flows.
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (!initialized || !window.gtag) return;
  try {
    window.gtag('event', eventName, params);
  } catch (err) {
    console.warn('[analytics] trackEvent failed:', eventName, err);
  }
}

/**
 * Hook that tracks page views on every React Router route change.
 * Should be called once in App.tsx (inside <BrowserRouter>).
 */
export function usePageTracking(): void {
  const location = useLocation();

  useEffect(() => {
    if (!initialized || !window.gtag) return;
    try {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    } catch (err) {
      console.warn('[analytics] page_view tracking failed:', err);
    }
  }, [location]);
}

/** Visible for testing — returns whether GA has been initialized. */
export function isGAInitialized(): boolean {
  return initialized;
}

/** Visible for testing — resets initialization state. */
export function resetGA(): void {
  initialized = false;
}
